import React, { useEffect, useMemo, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { ProgressQueue } from '@modernloop/shared/components';

import { FCWithFragments } from 'src/components/types';

import { useIsLastActivityLog } from 'src/entities/ActivityLog/ActivityLogListItemContext';
import ActivityLogBase from 'src/entities/ActivityLog/Helpers/ActivityLogBase';
import { ActivityLogBaseLike } from 'src/entities/ActivityLog/types';

interface Fragments {
  activityLog: {
    id: string;
    details?: ToolCallDetailsShape | null;
  };
}

type ToolCallStartInfo = {
  toolName: string;
  toolCallId: string;
  argumentsJson: string;
  /** Backend-resolved feed labels; empty string on rows written before labels existed. */
  displayLabelPresent?: string | null;
  displayLabelPast?: string | null;
};

type ToolCallCompletedInfo = {
  toolName: string;
  toolCallId: string;
  resultJson: string;
  isError: boolean;
};

type ToolCallDetailsShape = {
  __typename?: string;
  toolCalls?: ToolCallStartInfo[] | null;
  remainingToolCalls?: ToolCallStartInfo[] | null;
  completedToolCalls?: ToolCallCompletedInfo[] | null;
};

type Step = {
  toolCallId: string;
  title: string;
  status: 'completed' | 'pending' | 'error';
  message?: string | null;
};

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * Localized labels per tool. The backend Tool interface requires English labels
 * (AgentToolCallStartInfo.displayLabelPresent/Past), which serve as the fallback for tools that
 * do not have a translation entry yet — so a brand-new tool is always *named*, and gets
 * localized once its keys are added here and to the locale files.
 */
const TOOL_LABEL_KEYS: Record<string, { present: string; past: string }> = {
  GET_SCHEDULE_OPTIONS: {
    present: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.GET_SCHEDULE_OPTIONS.present',
    past: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.GET_SCHEDULE_OPTIONS.past',
  },
  SEARCH_EMPLOYEES: {
    present: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.SEARCH_EMPLOYEES.present',
    past: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.SEARCH_EMPLOYEES.past',
  },
  BOOK_INTERVIEW_SCHEDULE: {
    present: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.BOOK_INTERVIEW_SCHEDULE.present',
    past: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.BOOK_INTERVIEW_SCHEDULE.past',
  },
  SEND_SELF_SCHEDULE_REQUEST: {
    present: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.SEND_SELF_SCHEDULE_REQUEST.present',
    past: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.SEND_SELF_SCHEDULE_REQUEST.past',
  },
  SEND_AVAILABILITY_REQUEST: {
    present: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.SEND_AVAILABILITY_REQUEST.present',
    past: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.SEND_AVAILABILITY_REQUEST.past',
  },
  UPDATE_AVAILABILITY_REQUEST: {
    present: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.UPDATE_AVAILABILITY_REQUEST.present',
    past: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.UPDATE_AVAILABILITY_REQUEST.past',
  },
  CANCEL_AVAILABILITY_REQUEST: {
    present: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.CANCEL_AVAILABILITY_REQUEST.present',
    past: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.CANCEL_AVAILABILITY_REQUEST.past',
  },
  SET_CANDIDATE_AVAILABILITY: {
    present: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.SET_CANDIDATE_AVAILABILITY.present',
    past: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.SET_CANDIDATE_AVAILABILITY.past',
  },
  SEND_CANDIDATE_EMAIL: {
    present: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.SEND_CANDIDATE_EMAIL.present',
    past: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.SEND_CANDIDATE_EMAIL.past',
  },
  ASK_USER_QUESTION: {
    present: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.ASK_USER_QUESTION.present',
    past: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.ASK_USER_QUESTION.past',
  },
  UPDATE_SELF_SCHEDULE_REQUEST: {
    present: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.UPDATE_SELF_SCHEDULE_REQUEST.present',
    past: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.UPDATE_SELF_SCHEDULE_REQUEST.past',
  },
  CANCEL_SELF_SCHEDULE_REQUEST: {
    present: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.CANCEL_SELF_SCHEDULE_REQUEST.present',
    past: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.CANCEL_SELF_SCHEDULE_REQUEST.past',
  },
  SET_MEETING_LOCATION: {
    present: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.SET_MEETING_LOCATION.present',
    past: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.SET_MEETING_LOCATION.past',
  },
  CREATE_DEBRIEF: {
    present: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.CREATE_DEBRIEF.present',
    past: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.CREATE_DEBRIEF.past',
  },
  UPDATE_DEBRIEF: {
    present: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.UPDATE_DEBRIEF.present',
    past: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.UPDATE_DEBRIEF.past',
  },
  SCHEDULE_DEBRIEF: {
    present: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.SCHEDULE_DEBRIEF.present',
    past: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.SCHEDULE_DEBRIEF.past',
  },
  CANCEL_DEBRIEF: {
    present: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.CANCEL_DEBRIEF.present',
    past: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.CANCEL_DEBRIEF.past',
  },
  GET_DEBRIEF_OPTIONS: {
    present: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.GET_DEBRIEF_OPTIONS.present',
    past: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.GET_DEBRIEF_OPTIONS.past',
  },
  SEND_EMPLOYEE_EMAIL: {
    present: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.SEND_EMPLOYEE_EMAIL.present',
    past: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.SEND_EMPLOYEE_EMAIL.past',
  },
  SEND_EMPLOYEE_SLACK_MESSAGE: {
    present: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.SEND_EMPLOYEE_SLACK_MESSAGE.present',
    past: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.SEND_EMPLOYEE_SLACK_MESSAGE.past',
  },
  SEARCH_INTERVIEW_MODULE_MEMBERS: {
    present: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.SEARCH_INTERVIEW_MODULE_MEMBERS.present',
    past: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.SEARCH_INTERVIEW_MODULE_MEMBERS.past',
  },
  SEARCH_INTERVIEW_MODULES: {
    present: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.SEARCH_INTERVIEW_MODULES.present',
    past: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.SEARCH_INTERVIEW_MODULES.past',
  },
  SET_CANDIDATE_TIMEZONE: {
    present: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.SET_CANDIDATE_TIMEZONE.present',
    past: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.SET_CANDIDATE_TIMEZONE.past',
  },
  GET_REPLACEMENT_INTERVIEWER: {
    present: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.GET_REPLACEMENT_INTERVIEWER.present',
    past: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.GET_REPLACEMENT_INTERVIEWER.past',
  },
  REMEMBER: {
    present: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.REMEMBER.present',
    past: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.REMEMBER.past',
  },
  CANCEL_SINGLE_INTERVIEW: {
    present: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.CANCEL_SINGLE_INTERVIEW.present',
    past: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.CANCEL_SINGLE_INTERVIEW.past',
  },
  CANCEL_INTERVIEW_SCHEDULE: {
    present: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.CANCEL_INTERVIEW_SCHEDULE.present',
    past: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.CANCEL_INTERVIEW_SCHEDULE.past',
  },
  UPDATE_INTERVIEW: {
    present: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.UPDATE_INTERVIEW.present',
    past: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.UPDATE_INTERVIEW.past',
  },
  SET_INTERVIEW_PLAN: {
    present: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.SET_INTERVIEW_PLAN.present',
    past: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.SET_INTERVIEW_PLAN.past',
  },
  REPLACE_INTERVIEWERS: {
    present: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.REPLACE_INTERVIEWERS.present',
    past: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.REPLACE_INTERVIEWERS.past',
  },
  BOOK_ADDITIONAL_INTERVIEW: {
    present: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.BOOK_ADDITIONAL_INTERVIEW.present',
    past: 'hiring-dashboard-candidates:candidates.activityLog.agentTool.BOOK_ADDITIONAL_INTERVIEW.past',
  },
};

/** Localized title for a tool call: translation > backend label > generic localized copy. */
const toolCallTitle = (t: TranslateFn, toolCall: ToolCallStartInfo, tense: 'past' | 'present'): string => {
  const labelKey = TOOL_LABEL_KEYS[toolCall.toolName]?.[tense];
  if (labelKey) {
    return t(labelKey);
  }
  const backendLabel = tense === 'past' ? toolCall.displayLabelPast : toolCall.displayLabelPresent;
  if (backendLabel) {
    return backendLabel;
  }
  return t(
    tense === 'past'
      ? 'hiring-dashboard-candidates:candidates.activityLog.agentToolCalls.completedAnAction'
      : 'hiring-dashboard-candidates:candidates.activityLog.agentToolCalls.workingOnAction'
  );
};

/**
 * Localized description for a failed step. Prefers the backend's specific business message
 * (userMessage, set only for translated error-code messages), then localized generic copy keyed
 * off the machine-readable errorType. LLM-facing technical fields are never shown except for
 * legacy TOOL_ERROR rows that carried a business message before userMessage existed.
 */
const resolveErrorMessage = (t: TranslateFn, resultJson?: string): string => {
  const unexpected = t('hiring-dashboard-candidates:candidates.activityLog.agentToolCalls.errorUnexpected');
  if (!resultJson) {
    return unexpected;
  }
  let parsed: { userMessage?: string; error?: string; message?: string; errorCode?: string; errorType?: string };
  try {
    parsed = JSON.parse(resultJson) as typeof parsed;
  } catch {
    return unexpected;
  }
  if (parsed.userMessage) {
    return parsed.userMessage;
  }
  switch (parsed.errorType) {
    case 'INVALID_ARGUMENTS':
      return t('hiring-dashboard-candidates:candidates.activityLog.agentToolCalls.errorInvalidArguments');
    case 'TIMEOUT':
      return t('hiring-dashboard-candidates:candidates.activityLog.agentToolCalls.errorTimeout');
    case 'TOOL_NOT_FOUND':
    case 'TOOL_NOT_ALLOWED':
      return t('hiring-dashboard-candidates:candidates.activityLog.agentToolCalls.errorNotAvailable');
    case 'TOOL_CRASHED':
      return t('hiring-dashboard-candidates:candidates.activityLog.agentToolCalls.errorCrashed');
    case 'TOOL_ERROR':
      // Legacy rows carried the business message in `message` before userMessage existed; only
      // coded errors ever put user-safe copy there.
      return parsed.errorCode && parsed.message ? parsed.message : unexpected;
    default:
      return unexpected;
  }
};

const ActivityLogAgentToolCalls: FCWithFragments<Fragments> = ({ activityLog }) => {
  const { t } = useTranslation();
  const details = (activityLog as unknown as { details?: ToolCallDetailsShape | null }).details ?? null;
  const isLastActivityLog = useIsLastActivityLog();
  const [isExpandedByUser, setIsExpandedByUser] = useState<boolean | null>(null);

  const { toolCalls, completedToolCalls } = useMemo(() => {
    // eslint-disable-next-line no-underscore-dangle
    switch (details?.__typename) {
      case 'ActivityLogAgentToolCallsDetails':
        return {
          toolCalls: (details.toolCalls ?? []) as ToolCallStartInfo[],
          completedToolCalls: (details.completedToolCalls ?? []) as ToolCallCompletedInfo[],
        };
      case 'ActivityLogAgentToolCallsStartDetails':
        return {
          toolCalls: (details.toolCalls ?? []) as ToolCallStartInfo[],
          completedToolCalls: [] as ToolCallCompletedInfo[],
        };
      case 'ActivityLogAgentToolCallsContinueDetails':
        return {
          toolCalls: (details.remainingToolCalls ?? []) as ToolCallStartInfo[],
          completedToolCalls: (details.completedToolCalls ?? []) as ToolCallCompletedInfo[],
        };
      default:
        return {
          toolCalls: [] as ToolCallStartInfo[],
          completedToolCalls: [] as ToolCallCompletedInfo[],
        };
    }
  }, [details]);

  const steps = useMemo<Step[]>(() => {
    const completedById = new Map(completedToolCalls.map((call) => [call.toolCallId, call]));
    const plannedSteps = toolCalls.map((toolCall) => {
      const completed = completedById.get(toolCall.toolCallId);
      if (!completed) {
        return {
          toolCallId: toolCall.toolCallId,
          title: toolCallTitle(t, toolCall, 'present'),
          status: 'pending' as const,
          message: null,
        };
      }
      return {
        toolCallId: toolCall.toolCallId,
        title: toolCallTitle(t, toolCall, 'past'),
        status: completed.isError ? ('error' as const) : ('completed' as const),
        message: completed.isError ? resolveErrorMessage(t, completed.resultJson) : null,
      };
    });

    const orphanCompletedSteps = completedToolCalls
      .filter((call) => !toolCalls.some((toolCall) => toolCall.toolCallId === call.toolCallId))
      .map((call) => ({
        toolCallId: call.toolCallId,
        title: toolCallTitle(t, { toolName: call.toolName, toolCallId: call.toolCallId, argumentsJson: '' }, 'past'),
        status: call.isError ? ('error' as const) : ('completed' as const),
        message: call.isError ? resolveErrorMessage(t, call.resultJson) : null,
      }));

    return [...plannedSteps, ...orphanCompletedSteps];
  }, [completedToolCalls, t, toolCalls]);

  const { actionLabel, actionLabelKey, actionLabelValues } = useMemo(() => {
    if (steps.some((step) => step.status === 'pending')) {
      return {
        actionLabel: 'is taking actions',
        actionLabelKey: 'hiring-dashboard-candidates:candidates.activityLog.agentToolCalls.isTakingActions',
        actionLabelValues: undefined,
      };
    }

    const actionCount = steps.length;
    if (actionCount === 1) {
      return {
        actionLabel: 'took an action',
        actionLabelKey: 'hiring-dashboard-candidates:candidates.activityLog.agentToolCalls.tookAnAction',
        actionLabelValues: undefined,
      };
    }

    return {
      actionLabel: `took ${actionCount} actions`,
      actionLabelKey: 'hiring-dashboard-candidates:candidates.activityLog.agentToolCalls.tookActions',
      actionLabelValues: { total: actionCount },
    };
  }, [steps]);

  const hasPendingSteps = useMemo(() => steps.some((step) => step.status === 'pending'), [steps]);

  const autoExpanded = isLastActivityLog && hasPendingSteps;
  const isExpanded = isExpandedByUser ?? autoExpanded;

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes local state from external inputs; prefer derived state when possible.
  useEffect(() => {
    setIsExpandedByUser(null);
  }, [activityLog.id]);

  const queueItems = useMemo(() => {
    return steps.map((step) => ({
      id: step.toolCallId,
      title: step.title,
      description: step.message,
      status: step.status,
    }));
  }, [steps]);

  if (!steps.length) {
    return null;
  }

  return (
    <ActivityLogBase
      activityLog={activityLog as unknown as ActivityLogBaseLike}
      actionLabel={actionLabel}
      actionLabelKey={actionLabelKey}
      actionLabelValues={actionLabelValues}
      collapsible
      isExpanded={isExpanded}
      onExpandedChange={setIsExpandedByUser}
    >
      <ProgressQueue items={queueItems} sx={{ mt: 0.25 }} />
    </ActivityLogBase>
  );
};

ActivityLogAgentToolCalls.fragments = {
  activityLog: gql`
    ${ActivityLogBase.fragments.activityLog}
    fragment ActivityLogAgentToolCalls_activityLog on ActivityLog {
      id
      details {
        ... on ActivityLogAgentToolCallsDetails {
          __typename
          toolCalls {
            toolName
            toolCallId
            argumentsJson
            displayLabelPresent
            displayLabelPast
          }
          completedToolCalls {
            toolName
            toolCallId
            resultJson
            isError
          }
        }
        ... on ActivityLogAgentToolCallsStartDetails {
          __typename
          toolCalls {
            toolName
            toolCallId
            argumentsJson
            displayLabelPresent
            displayLabelPast
          }
        }
        ... on ActivityLogAgentToolCallsContinueDetails {
          __typename
          remainingToolCalls {
            toolName
            toolCallId
            argumentsJson
            displayLabelPresent
            displayLabelPast
          }
          completedToolCalls {
            toolName
            toolCallId
            resultJson
            isError
          }
        }
      }
      ...ActivityLogBase_activityLog
    }
  `,
};

export default ActivityLogAgentToolCalls;
